import * as icons from '../../../../assets/icons'
import { CRM, CRMLabels } from '@dtx-company/true-common/src/types/crmThirdParty'
import { FormFields, IntegrationInputConfig, IntegrationsInputsArgs } from '../types'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TextField, Box } from '@dtx-company/design-system/src'
import { accountHasHubSpotApiKey } from '@dtx-company/inter-app/src/redux/slices/pageEditor/hubSpotEditing'
import {
  flowpageIconFilled,
  hubspotColor,
  klaviyoColor,
  mailchimpColor,
  zapierColor
} from '@dtx-company/app/shared/code/src/assets'
import { getPlanTypeName } from '@dtx-company/app/shared/common/src/utils/getPlanTypeName'
import { stopPropagationEvents } from '../../../../components/profile/PageEditor/components/LinkEditDrawer/LinkEditDrawer.utils'
import { testZapierWebhook } from '../utils'
import { useClientHasMailchimpKey } from '@dtx-company/app/shared/common/src/hooks/useClientHasMaillchimpKey'
import { useDebouncedCallback } from 'use-debounce'
import { useState } from 'react'
import AccountSettingInputSection from '../subcomponents/AccountSettingInputSection'

export const useIntegrationsInputsConfigByCRM = ({
  formValues,
  setValue,
  setError,
  errors
}: IntegrationsInputsArgs): Record<CRM, IntegrationInputConfig> => {
  const [showGoogleSheetsCopy, setShowGoogleSheetsCopy] = useState(false)
  const mailchimpApiKey = useClientHasMailchimpKey()
  const hasMailchimpApiKey = Boolean(mailchimpApiKey)

  const testZapierWebhookDebounced = useDebouncedCallback(async (webhookUrl: string) => {
    try {
      await testZapierWebhook(webhookUrl)
      setError(FormFields.ZAPIER, {
        type: 'info',
        message:
          'We sent some data to your webhook to test it. Check your zap to verify the test was successful'
      })
    } catch {
      setError(FormFields.ZAPIER, {
        type: 'error',
        message:
          'We attempted to send some data to your webhook but we were unsuccessful. Please make sure the URL is complete and check for typos.'
      })
    }
  }, 300)
  return {
    [CRM.FLOWPAGE]: {
      label: CRMLabels.FLOWPAGE,
      checked: true,
      disabled: true,
      labelProps: { color: 'black' },
      iconSrc: flowpageIconFilled,
      postScript: '(see in Settings)',
      checkBoxOnChange: () => {
        //pass
      }
    },
    [CRM.GOOGLESHEETS]: {
      label: CRMLabels.GOOGLESHEETS,
      checked: formValues.googleSheets.enabled,
      iconSrc: icons.googlesheets,
      postScript: '(see in Settings)',
      labelProps: { color: 'black' },
      checkBoxOnChange: () => {
        if ((formValues.googleSheets.url ?? '').length > 0) {
          setValue(FormFields.GOOGLESHEETS, {
            ...formValues.googleSheets,
            enabled: !formValues.googleSheets.enabled
          })
        } else {
          setShowGoogleSheetsCopy(true)
        }
      },
      gatedFeatureTooltipText: `Upgrade to ${getPlanTypeName(
        PlanType.PRO
      )} to connect CRM data that you collect with Flowpage directly to Google Sheets.`,
      inputSection: showGoogleSheetsCopy ? (
        <Text marginBottom="14px" padding="0">
          Visit the Settings tab after saving this link to start collecting contacts in a Google
          Sheet.
        </Text>
      ) : undefined
    },
    [CRM.ZAPIER]: {
      label: CRMLabels.ZAPIER,
      checked: formValues.zapier.enabled,
      iconSrc: zapierColor,
      labelProps: { color: 'black' },
      checkBoxOnChange: () => setValue(FormFields.ZAPIER, { enabled: !formValues.zapier.enabled }),
      gatedFeatureTooltipText: `Upgrade to ${getPlanTypeName(
        PlanType.PRO_PLUS_ORG
      )} to connect CRM data that you collect with Flowpage to your other apps with Zapier.`,
      inputSection: (
        <>
          <Input
            labelProps={{ marginBottom: '8px' }}
            maxWidth="100%"
            type="text"
            label="Zapier Custom Webhook URL"
            placeholder="Enter custom webhook URL"
            value={formValues.zapier.url}
            onChange={e => {
              setValue(
                FormFields.ZAPIER,
                { ...formValues.zapier, url: e.target.value },
                { shouldValidate: true }
              )
              testZapierWebhookDebounced(e.target.value)
            }}
            {...stopPropagationEvents}
          />
          {errors.zapier && (
            <Text
              mt="8px"
              color={errors.zapier.type === 'error' ? 'status.errorDark' : 'status.infoDark'}
            >
              {errors.zapier?.message}
            </Text>
          )}
          <Spacer mb="16px" />
        </>
      )
    },
    [CRM.MAILCHIMP]: {
      label: CRMLabels.MAILCHIMP,
      checked: formValues.mailChimp.enabled,
      iconSrc: mailchimpColor,
      labelProps: { color: 'black' },
      checkBoxOnChange: () => {
        // Intentionally resets mailChimp (including listId) when toggling; audience ID is re-entered if enabled again.
        // API key lives at the page/Settings level, not on the form actionData.
        setValue(FormFields.MAILCHIMP, { enabled: !formValues.mailChimp.enabled })
      },
      tooltip: {
        content:
          'A Mailchimp integration is setup once and can be used on multiple CRM forms.\n' +
          'Set up or edit the API key in the Settings tab.\n' +
          'Optionally add an Audience ID to send form responses to a specific Mailchimp audience; ' +
          'leave it blank to use the first audience in the account.'
      },
      gatedFeatureTooltipText: `Upgrade to ${getPlanTypeName(
        PlanType.PRO
      )} to connect CRM data that you collect with Flowpage directly to your Mailchimp instance.`,
      inputSection: (
        <Box {...stopPropagationEvents} sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Audience ID (optional)"
            placeholder="Enter Mailchimp Audience ID"
            value={formValues.mailChimp.listId ?? ''}
            disabled={!hasMailchimpApiKey}
            error={errors.mailChimp?.type === 'error'}
            helperText={errors.mailChimp?.message}
            onChange={e => {
              setValue(
                FormFields.MAILCHIMP,
                { ...formValues.mailChimp, listId: e.target.value },
                { shouldValidate: true }
              )
            }}
          />
        </Box>
      )
    },
    [CRM.KLAVIYO]: {
      label: CRMLabels.KLAVIYO,
      checked: formValues.klaviyo.enabled,
      iconSrc: klaviyoColor,
      labelProps: { color: 'black' },
      checkBoxOnChange: () =>
        setValue(FormFields.KLAVIYO, {
          ...formValues.klaviyo,
          enabled: !formValues.klaviyo.enabled
        }),
      gatedFeatureTooltipText: `Upgrade to ${getPlanTypeName(
        PlanType.PRO_FLEX
      )} to connect CRM data that you collect with Flowpage directly to your Klaviyo instance.`,
      inputSection: (
        <>
          <Text>
            This feature collects email addresses. The email field will be set to required.
          </Text>
          <Spacer mb="8px" />
          <Input
            labelProps={{ marginBottom: '8px' }}
            maxWidth="100%"
            type="text"
            label="Klaviyo API Key"
            placeholder="Enter Klaviyo API Key"
            value={formValues.klaviyo.url}
            onChange={e => {
              setValue(
                FormFields.KLAVIYO,
                { ...formValues.klaviyo, url: e.target.value },
                { shouldValidate: true }
              )
            }}
            {...stopPropagationEvents}
          />
          <Input
            labelProps={{ marginBottom: '8px' }}
            maxWidth="100%"
            type="text"
            label="Klaviyo List ID"
            placeholder="List ID (optional)"
            value={formValues.klaviyo.customListId}
            onChange={e => {
              setValue(
                FormFields.KLAVIYO,
                { ...formValues.klaviyo, customListId: e.target.value },
                { shouldValidate: true }
              )
            }}
            {...stopPropagationEvents}
          />
          {errors.klaviyo && (
            <Text
              mt="8px"
              color={errors.zapier.type === 'error' ? 'status.errorDark' : 'status.infoDark'}
            >
              {errors.klaviyo?.message}
            </Text>
          )}
          <Spacer mb="16px" />
        </>
      )
    },
    [CRM.HUBSPOT]: {
      label: CRMLabels.HUBSPOT,
      checked: formValues.hubspot?.enabled ?? false,
      iconSrc: hubspotColor,
      labelProps: { color: 'black' },
      checkBoxOnChange: () => {
        setValue(FormFields.HUBSPOT, {
          ...formValues.hubspot,
          enabled: !formValues.hubspot.enabled
        })
      },
      tooltip: {
        content:
          'A HubSpot integration is setup once and can be used on multiple CRM forms, even in different Flowpages. In order to edit or setup the integration, do so in the Settings tab'
      },
      gatedFeatureTooltipText: `Upgrade to ${getPlanTypeName(
        PlanType.PRO_FLEX
      )} to connect CRM data that you collect with Flowpage directly to your Hubspot instance.`,

      inputSection: (
        <AccountSettingInputSection
          provider="HubSpot"
          checkAccountCallback={accountHasHubSpotApiKey}
          actionData={formValues.hubspot}
        />
      )
    }
  }
}
